// An entity's lifeline: every migration that added, changed, renamed or removed it.
import { History, Loader2 } from 'lucide-react';
import { cn } from '../lib/cn';
import { openTimeline } from '../state/dock';
import type { ModelRef } from '../state/workspace';
import { stepDate } from './Scrubber';
import { entityEvents } from './stepGraph';
import { ensureTimeline, stepIndexOf, useTimeline, useTimelineProgress } from './timeline';

const DOT: Record<string, string> = { added: 'bg-added', removed: 'bg-removed', changed: 'bg-changed' };

export function EntityHistory({ modelRef, entityKey }: { modelRef: ModelRef; entityKey: string }) {
  const timeline = useTimeline(modelRef.setKey, false);
  const progress = useTimelineProgress(modelRef.setKey);
  if (!timeline) {
    return progress ? (
      <p className="flex items-center gap-2 text-[12px] text-muted">
        <Loader2 size={13} className="animate-spin" /> Reading migrations · {progress.done} of {progress.total}
      </p>
    ) : (
      <button onClick={() => void ensureTimeline(modelRef.setKey)} className="flex items-center gap-1.5 text-[12px] text-accent hover:underline">
        <History size={13} /> Show when this entity changed
      </button>
    );
  }
  const at = stepIndexOf(timeline, modelRef);
  const id = at >= 0 ? timeline.ids[at]?.get(entityKey) : undefined;
  const events = id ? entityEvents(timeline, id) : [];
  if (events.length === 0) return <p className="text-[12px] text-faint">No changes found in the migrations.</p>;
  return (
    <ol className="relative ml-1 border-l border-border" data-testid="entity-history">
      {events.map(({ index, group }) => {
        const step = timeline.steps[index]!;
        const items = group.items.length ? group.items : [{ text: group.state === 'added' ? 'Added' : 'Removed', state: group.state }];
        return (
          <li key={step.id} className="relative pb-2 pl-3.5">
            <span className={cn('absolute top-[7px] -left-[4.5px] h-2 w-2 rounded-full ring-2 ring-panel', DOT[group.state])} aria-hidden />
            <button onClick={() => openTimeline(modelRef.setKey, step.id)} className="flex w-full items-baseline gap-2 text-left text-[12.5px] hover:underline">
              <span className="min-w-0 truncate font-medium">{step.name}</span>
              <span className="ml-auto flex-none text-[11px] text-faint">{stepDate(step)}</span>
            </button>
            <ul className="text-[12px] text-muted">
              {items.slice(0, 4).map((item, i) => (
                <li key={i} className="truncate" title={item.text}>
                  {item.text}
                </li>
              ))}
              {items.length > 4 && <li className="text-faint">and {items.length - 4} more</li>}
            </ul>
          </li>
        );
      })}
    </ol>
  );
}
